import { css, cva, sva } from "@stubhub/broadway-foundation/css";

export const bodyWrapperRecipe = cva({
  base: {
    width: "full",
    height: "full",
    marginBlockEnd: "primitives.150",
    paddingBlockEnd: "primitives.400",
  },
  variants: {
    surface: {
      none: {},
      white: { backgroundColor: "bg" },
    },
    shoppingMode: {
      standard: {},
      ga: {
        paddingBlockEnd: "primitives.100",
        marginBlockEnd: "primitives.0",
      },
    },
  },
  defaultVariants: { surface: "none", shoppingMode: "standard" },
});

// fullWidthDiv — replaces FullWidthDiv styled.div.
export const fullWidthDiv = css({
  width: "full",
});

// bodyLayout — sva recipe for the Container → Row → Col structure.
//
// Replaces three pairs of conflicting css() + cx() combinations that caused
// atomic CSS cascade nondeterminism (CWP-1981). Property conflicts between
// separate css() calls combined via cx() are resolved by Panda's stylesheet
// generation order, NOT by the order in the cx() call — a race condition.
//
// The sva recipe eliminates this by:
// 1. Using variant overrides (guaranteed to win over base in recipe layer)
// 2. Using desktop: condition for display:flex (CSS handles viewport, not JS)
//
// layout variant:
//   "default" — max-width constrained container with inline padding
//   "fluid"   — full-width, no padding (calendar view)
const bodyLayoutSlots = ["container", "row", "col"] as const;

export const bodyLayoutRecipe = sva({
  slots: bodyLayoutSlots,
  className: "bodyLayout",
  base: {
    container: {
      boxSizing: "content-box",
      marginInlineStart: "auto",
      marginInlineEnd: "auto",
      paddingInlineStart: "primitives.100",
      paddingInlineEnd: "primitives.100",
      desktop: {
        maxWidth: "[1200px]",
        paddingInlineStart: "primitives.200",
        paddingInlineEnd: "primitives.200",
      },
    },
    row: {
      display: "flex",
      flexGrow: 1,
      flexShrink: 0,
      flexBasis: "auto",
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: "space-between",
      paddingBlockStart: "primitives.0",
      paddingBlockEnd: "primitives.0",
      paddingInlineStart: "primitives.50",
      paddingInlineEnd: "primitives.50",
    },
    col: {
      flexGrow: 0,
      flexShrink: 1,
      flexBasis: "full",
      display: "block",
      boxSizing: "border-box",
      maxWidth: "full",
      desktop: {
        display: "flex",
      },
    },
  },
  variants: {
    layout: {
      default: {},
      residency: {
        container: {
          desktop: {
            paddingInlineEnd: "primitives.400",
          },
        },
      },
      fluid: {
        container: {
          maxWidth: "full",
          paddingInlineStart: "primitives.0",
          paddingInlineEnd: "primitives.0",
          desktop: {
            paddingInlineStart: "primitives.0",
            paddingInlineEnd: "primitives.0",
          },
        },
        row: {
          paddingInlineStart: "primitives.0",
          paddingInlineEnd: "primitives.0",
        },
      },
    },
  },
  defaultVariants: {
    layout: "default",
  },
});

// Standalone container class for sections outside the bodyLayout recipe
// (e.g. PerformerCarouselSection). Same values as the recipe's container base.
// Safe to use alone — no cx() conflict since there's no override class.
export const styledContainer = css({
  boxSizing: "content-box",
  marginInlineStart: "auto",
  marginInlineEnd: "auto",
  paddingInlineStart: "primitives.200",
  paddingInlineEnd: "primitives.200",
  desktop: {
    maxWidth: "[1200px]",
    paddingInlineStart: "primitives.400",
    paddingInlineEnd: "primitives.400",
  },
});

export const modernizedInfoCardSpacing = css({ paddingBlockStart: "primitives.200" });

// calendarFiltersContainer — replaces styled(Container) for the sticky
// calendar filter bar.
// padding-block: spacing(4) = 16px = primitives.200
// margin-block-end: spacing(-5) = -20px = -primitives.250
// background: white = bg.surface
// Container base padding is preserved (same as bodyLayout container slot).
// Only rendered on desktop (inside isDesktopCalendarViewVisible), so desktop
// values (max-width 1200px, 32px inline padding) are the base — no breakpoint needed.
export const calendarFiltersContainer = css({
  position: "sticky",
  insetBlockStart: "primitives.0",
  paddingBlockStart: "primitives.200",
  paddingBlockEnd: "primitives.200",
  marginBlockEnd: "-primitives.250",
  backgroundColor: "bg.surface",
  zIndex: "[1]",
  maxWidth: "[1200px]",
  boxSizing: "content-box",
  marginInlineStart: "auto",
  marginInlineEnd: "auto",
});

export const eventGridV2BackgroundDivRecipe = cva({
  base: {
    backgroundColor: "bg.surface.strong",
  },
  variants: {
    positioning: {
      static: {},
      relative: { position: "relative" },
    },
  },
  defaultVariants: { positioning: "static" },
});

// buttonsContainer — flex row for the mobile glass header buttons.
// margin-left: spacing(31) = 124px — no token, escape hatch.
// margin-right: spacing(3.5) = 14px = primitives.175.
// flexSpaceBetween is always true at the single usage site.
export const buttonsContainer = css({
  marginInlineStart: "[124px]",
  display: "flex",
  justifyContent: "space-between",
  marginInlineEnd: "primitives.175",
});

// styledAboutButton — replaces styled.button.
// border-radius: xxl = 24px = primitives.300 (radii)
// background: white = bg.surface
// color: textPrimary = text
// typography: subtitle2 ~ font-size:14px, font-weight:medium, line-height:20px
// border: 1px solid #cfd4d9 — no semantic token, escape hatch
// margin-top: spacing(1) = 4px = primitives.50
// padding: spacing(1, 3) = 4px 12px = primitives.50 primitives.150
export const styledAboutButton = css({
  borderStartStartRadius: "primitives.300",
  borderStartEndRadius: "primitives.300",
  borderEndStartRadius: "primitives.300",
  borderEndEndRadius: "primitives.300",
  backgroundColor: "bg.surface",
  color: "text",
  fontSize: "primitives.350",
  fontWeight: "medium",
  lineHeight: "primitives.450",
  borderBlockStartWidth: "thin",
  borderBlockEndWidth: "thin",
  borderInlineStartWidth: "thin",
  borderInlineEndWidth: "thin",
  borderBlockStartStyle: "solid",
  borderBlockEndStyle: "solid",
  borderInlineStartStyle: "solid",
  borderInlineEndStyle: "solid",
  // No semantic token — border=#dfe3e5, border.hover=#cfd5d9 (approximate but not exact)
  borderBlockStartColor: "[#cfd4d9]",
  borderBlockEndColor: "[#cfd4d9]",
  borderInlineStartColor: "[#cfd4d9]",
  borderInlineEndColor: "[#cfd4d9]",
  marginBlockStart: "primitives.50",
  paddingBlockStart: "primitives.50",
  paddingBlockEnd: "primitives.50",
  paddingInlineStart: "primitives.150",
  paddingInlineEnd: "primitives.150",
  cursor: "pointer",
});
